import * as React from 'react';
import { useState } from 'react';
import { Box, Button, Typography, TextareaAutosize, styled, Stack, IconButton } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SparklesIcon from '@mui/icons-material/AutoAwesome';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
const StyledTextarea = styled(TextareaAutosize)(({ theme }) => ({
    width: '100%',
    padding: theme.spacing(1.5, 2),
    borderRadius: theme.shape.borderRadius,
    border: `2px solid ${theme.palette.grey[300]}`,
    transition: 'all 0.2s',
    resize: 'none',
    fontFamily: theme.typography.fontFamily,
    '&:focus': {
        borderColor: theme.palette.primary.main,
        boxShadow: `${theme.palette.primary.light} 0 0 0 3px`,
        outline: 'none',
    },
}));

export default function WhyWhyWhyAnalysis() {
    const [problemStatement, setProblemStatement] = useState('');
    const [whyLevels, setWhyLevels] = useState([
        { id: 1, question: 'Why did this problem occur?', answer: '' }
    ]);

    const addWhyLevel = () => {
        if (whyLevels.length < 5) {
            setWhyLevels([
                ...whyLevels,
                {
                    id: whyLevels.length + 1,
                    question: `Why did "${whyLevels[whyLevels.length - 1].answer || 'the previous answer'}" occur?`,
                    answer: ''
                }
            ]);
        }
    };

    const updateAnswer = (id, answer) => {
        setWhyLevels(whyLevels.map(level =>
            level.id === id ? { ...level, answer } : level
        ));
    };

    const removeWhyLevel = (id) => {
        if (whyLevels.length > 1) {
            setWhyLevels(whyLevels.filter(level => level.id !== id));
        }
    };

    const aiSuggest = (levelId) => {
        const suggestions = [
            'Inadequate training provided to operators',
            'Equipment maintenance schedule not followed',
            'Insufficient quality control checkpoints',
            'Communication breakdown between departments',
            'Lack of standard operating procedures',
            'Material specifications not verified',
            'Environmental conditions not controlled',
            'Resource constraints affecting process'
        ];

        const randomSuggestion = suggestions[Math.floor(Math.random() * suggestions.length)];
        updateAnswer(levelId, randomSuggestion);
    };

    const generateReport = () => {
        const reportContent = `
WHY-WHY ANALYSIS REPORT
Generated: ${new Date().toLocaleString()}
 
PROBLEM STATEMENT:
${problemStatement || 'Not specified'}
 
ANALYSIS:
${whyLevels.map((level, index) => `
WHY #${index + 1}: ${level.question}
ANSWER: ${level.answer || 'Not answered'}
`).join('\n')}
 
ROOT CAUSE IDENTIFIED:
${whyLevels[whyLevels.length - 1]?.answer || 'Analysis incomplete'}
 
---
Generated by NC Pro - AI-Powered Non-Conformity Management Tool
www.ncpro.gbinternational.sg
    `.trim();

        const blob = new Blob([reportContent], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `why-why-analysis-${Date.now()}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const isAddButtonDisabled = !whyLevels[whyLevels.length - 1]?.answer;
    const isReportButtonDisabled = !problemStatement || !whyLevels.some(l => l.answer);

    return (
        <Box sx={{ p: 4, bgcolor: 'white', borderRadius: 2 }}>
            <Box sx={{ mb: 4 }}>
                <Typography component="label" variant="subtitle1" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1, display: 'block' }}>
                    Problem Statement
                </Typography>
                <StyledTextarea
                    value={problemStatement}
                    onChange={(e) => setProblemStatement(e.target.value)}
                    placeholder="Describe the problem you want to analyze..."
                    minRows={3}
                    sx={{
                        '&:focus': { borderColor: 'info.main', boxShadow: (theme) => `${theme.palette.info.light} 0 0 0 3px` },
                    }}
                />
            </Box>

            <Stack spacing={3} sx={{ mb: 4 }}>
                {whyLevels.map((level, index) => (
                    <Box key={level.id} sx={{ position: 'relative' }}>
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                            <Box
                                sx={{
                                    flexShrink: 0,
                                    width: 48,
                                    height: 48,
                                    background: 'linear-gradient(to bottom right, #06B6D4, #2563EB)',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontWeight: 700,
                                    boxShadow: 3,
                                    fontSize: '1.125rem',
                                }}
                            >
                                {index + 1}
                            </Box>

                            <Box sx={{ flexGrow: 1, pr: whyLevels.length > 1 ? 0 : 4 }}>
                                <Stack spacing={1.5}>
                                    <Box
                                        sx={{
                                            background: 'linear-gradient(to right, #ECFEFF, #EEF2FF)',
                                            borderRadius: 1,
                                            p: 2,
                                            border: '1px solid #A5F3FC',
                                        }}
                                    >
                                        <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                            {level.question}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ position: 'relative' }}>
                                        <StyledTextarea
                                            value={level.answer}
                                            onChange={(e) => updateAnswer(level.id, e.target.value)}
                                            placeholder="Enter your answer..."
                                            minRows={2}
                                            sx={{
                                                pr: 8,
                                                '&:focus': { borderColor: 'primary.main', boxShadow: (theme) => `${theme.palette.primary.light} 0 0 0 3px` },
                                            }}
                                        />
                                        <Button
                                            onClick={() => aiSuggest(level.id)}
                                            sx={{
                                                position: 'absolute',
                                                bottom: 12,
                                                right: 12,
                                                minWidth: 0,
                                                p: 1.2,
                                                background: 'linear-gradient(to right, #A855F7, #EC4899)',
                                                color: 'white',
                                                borderRadius: 1,
                                                boxShadow: 3,
                                                '&:hover': {
                                                    background: 'linear-gradient(to right, #9333EA, #D41460)',
                                                    boxShadow: 6,
                                                },
                                            }}
                                            title="AI Suggestion"
                                        >
                                            <SparklesIcon sx={{ width: 16, height: 16 }} />
                                        </Button>
                                    </Box>
                                </Stack>
                            </Box>

                            {whyLevels.length > 1 && (
                                <IconButton
                                    onClick={() => removeWhyLevel(level.id)}
                                    sx={{
                                        flexShrink: 0,
                                        color: 'error.main',
                                        p: 1,
                                        '&:hover': { bgcolor: 'error.light', opacity: 0.8 },
                                    }}
                                    aria-label="Remove Why Level"
                                >
                                    <DeleteIcon sx={{ width: 20, height: 20 }} />
                                </IconButton>
                            )}
                        </Stack>

                        {index < whyLevels.length - 1 && (
                            <Box
                                sx={{
                                    ml: '24px',
                                    my: 1.5,
                                    height: 32,
                                    width: 3,
                                    background: 'linear-gradient(to bottom, #67E8F9, #93C5FD)',
                                    position: 'relative',
                                    left: 0,
                                }}
                            />
                        )}
                    </Box>
                ))}
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                {whyLevels.length < 5 && (
                    <Button
                        onClick={addWhyLevel}
                        disabled={isAddButtonDisabled}
                        startIcon={<AddIcon />}
                        variant="contained"
                        fullWidth

                        size="large"
                        sx={{
                            background: 'linear-gradient(to right, #06B6D4, #2563EB)',
                            color: 'white',
                            py: 1.5,
                            fontWeight: 600,
                            textTransform: 'none',
                            '&:hover': {
                                background: 'linear-gradient(to right, #0891B2, #1D4ED8)',
                            },
                        }}
                    >
                        Add Next "Why" Level ({whyLevels.length}/5)
                    </Button>
                )}

                <Button
                    onClick={generateReport}
                    disabled={isReportButtonDisabled}
                    startIcon={<DownloadIcon />}
                    variant="contained"
                    fullWidth
                    size="large"
                    sx={{
                        background: 'linear-gradient(to right, #10B981, #059669)',
                        color: 'white',
                        py: 1.5,
                        fontWeight: 600,
                        boxShadow: 6,
                        textTransform: 'none',
                        '&:hover': {
                            background: 'linear-gradient(to right, #0F7F64, #047857)',
                            boxShadow: 8,
                        },
                    }}
                >
                    Generate Instant Report
                </Button>
            </Stack>

            {whyLevels.length === 5 && whyLevels.every(l => l.answer) && (
                <Box
                    sx={{
                        mt: 4,
                        background: 'linear-gradient(to right, #F0FFF4, #F0FFF7)',
                        border: '2px solid #B7E4C7',
                        borderRadius: 1,
                        p: 3,
                    }}
                >

                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.dark', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 24, height: 24, bgcolor: 'success.main', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CheckCircleIcon sx={{ color: 'white', fontSize: 16 }} />
                        </Box>
                        Root Cause Identified
                    </Typography>
                    <Typography sx={{ color: 'success.dark' }}>
                        {whyLevels[whyLevels.length - 1].answer}
                    </Typography>
                </Box>
            )}
        </Box>
    );
}
